import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import type { BranchNameType, ModuleIdType } from '../types.js'
import { jsonProperty, jsonClass } from '@sumbricht/json-class-serializer'

export const BuildModes = {
	dev: 'Development',
	prod: 'Production (optimized)',
	default: 'Default',
	external: "External (don't use)",
} as const
export type BuildMode = keyof typeof BuildModes
export type BuildStatusName = 'Stopped' | 'Has errors' | 'Building' | 'Running'
export interface BuildStatus {
	status: BuildStatusName
	fgColor: string
	bgColor: string
}
export const BuildStatuses: Record<BuildStatusName, BuildStatus> = {
	Stopped: { status: 'Stopped', fgColor: 'silver', bgColor: 'lightgray' },
	'Has errors': { status: 'Has errors', fgColor: 'red', bgColor: 'salmon' },
	Building: { status: 'Building', fgColor: '#1c518b', bgColor: 'lightskyblue' },
	Running: { status: 'Running', fgColor: 'green', bgColor: 'lightgreen' },
}
export const StartBehaviors = {
	auto: 'Autostart',
	manual: 'Manual',
	onSave: 'On save',
} as const
export type StartBehavior = keyof typeof StartBehaviors

@jsonClass('BuildConfiguration')
export class BuildConfiguration {
	@jsonProperty(String)
	get __type(): string {
		return 'BuildConfiguration'
	}
	set __type(_) {
		/*ignore*/
	}

	@jsonProperty(String)
	branchName!: BranchNameType
	@jsonProperty(String)
	appModuleId!: ModuleIdType
	@jsonProperty(String)
	startBehavior: StartBehavior = 'manual'
	@jsonProperty(Boolean)
	isActive: boolean = false
	@jsonProperty(Boolean)
	isImmediateStartRequested: boolean = false
	@jsonProperty(Boolean)
	isInitialBuildDone: boolean = false
	@jsonProperty(Boolean)
	isBuildDone: boolean = false
	@jsonProperty(String)
	buildMode: BuildMode = 'default'
	@jsonProperty(String)
	buildError: string | null = null

	constructor(init?: Partial<BuildConfiguration>) {
		assignCommonProperties(this, init)
	}

	updateConfig(from: BuildConfiguration) {
		this.startBehavior = from.startBehavior
		this.isActive = from.isActive
		this.buildMode = from.buildMode
		this.isImmediateStartRequested = from.isImmediateStartRequested
	}

	updateStatus(from: BuildConfiguration) {
		this.isBuildDone = from.isBuildDone
		this.isInitialBuildDone = from.isInitialBuildDone
		this.buildError = from.buildError
	}

	getBuildStatus(): BuildStatus {
		if (!this.isActive) return BuildStatuses['Stopped']
		if (this.buildError) return BuildStatuses['Has errors']
		if (!this.isBuildDone) return BuildStatuses['Building']
		return BuildStatuses['Running']
	}

	matches(other: BuildConfiguration) {
		return (
			this.branchName == other.branchName &&
			this.appModuleId == other.appModuleId
		)
	}
}
